import { styled } from '@mui/material/styles'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import Box from '@/components/common/Box'
import { spacing, sizing, colors, effects, transitions, typography } from '@/styles/designTokens'

export const LayoutGrid = styled(Box)(({ theme }) => ({
	width: '100%',
	maxWidth: sizing.layoutMaxWidth,
	marginLeft: 'auto',
	marginRight: 'auto',
	display: 'grid',
	gridTemplateColumns: 'minmax(0, 1fr)',
	alignItems: 'start',
	[theme.breakpoints.up('lg')]: {
		gridTemplateColumns: `minmax(0, 1fr) ${sizing.cartSidebarWidth}px`,
		gap: spacing.xxxl,
		paddingLeft: spacing.xxxl,
		paddingRight: spacing.xxxl,
	},
	[theme.breakpoints.between('lg', 'lgNarrow')]: {
		gridTemplateColumns: `minmax(0, 1fr) ${sizing.cartSidebarWidthNarrow}px`,
		gap: spacing.lg,
		paddingLeft: spacing.md,
		paddingRight: spacing.md,
	},
	[theme.breakpoints.up('xl')]: {
		gridTemplateColumns: `minmax(0, 1fr) ${sizing.cartSidebarWidthWide}px`,
	},
	[theme.breakpoints.up('xxl')]: {
		paddingLeft: 0,
		paddingRight: 0,
	},
}))

export const StickyHeader = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isCompact',
})<{ isCompact?: boolean }>(({ theme, isCompact }) => ({
	position: 'sticky',
	top: 0,
	zIndex: theme.zIndex.stickyHeader,
	width: '100%',
	backgroundColor: isCompact ? colors.surfaceMutedFrosted : colors.surfaceMuted,
	backdropFilter: isCompact ? effects.backdropBlur : 'none',
	WebkitBackdropFilter: isCompact ? effects.backdropBlur : 'none',
	transition: `background-color ${transitions.spatial}`,
}))

export const MainContent = styled('main')(({ theme }) => ({
	// Prevent the fixed CartBottomBar from obscuring the last row of items on mobile/tablet.
	// The bar is hidden at lg+ where the sidebar takes over, so the padding is only needed below lg.
	paddingBottom: spacing.pageBottomPadding,
	[theme.breakpoints.up('lg')]: {
		paddingBottom: 0,
	},
}))

export const CalculateErrorBanner = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.sm,
	// Separates this banner from OffHoursWarningBanner directly above it. Both share
	// colors.errorBg, so without a gap the two stacked banners read as one two-line
	// error instead of two distinct conditions (client-side off-hours vs API SCHDNA).
	marginTop: spacing.sm,
	paddingTop: spacing.md,
	paddingBottom: spacing.md,
	paddingLeft: spacing.lg,
	paddingRight: spacing.lg,
	backgroundColor: colors.errorBg,
	color: colors.errorFigma,
	[theme.breakpoints.up('md')]: {
		maxWidth: sizing.tabletContentMaxWidth,
		marginLeft: 'auto',
		marginRight: 'auto',
	},
	[theme.breakpoints.up('lg')]: {
		maxWidth: 'none',
		marginLeft: 'unset',
		marginRight: 'unset',
	},
}))

export const CalculateErrorText = styled('p')({
	margin: 0,
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingNormal,
	color: colors.textPrimary,
})

export const OffHoursWarningBanner = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.sm,
	paddingTop: spacing.md,
	paddingBottom: spacing.md,
	paddingLeft: spacing.lg,
	paddingRight: spacing.lg,
	backgroundColor: colors.errorBg,
	color: colors.errorFigma,
	[theme.breakpoints.up('md')]: {
		maxWidth: sizing.tabletContentMaxWidth,
		marginLeft: 'auto',
		marginRight: 'auto',
	},
	[theme.breakpoints.up('lg')]: {
		maxWidth: 'none',
		marginLeft: 'unset',
		marginRight: 'unset',
	},
}))

export const OffHoursWarningText = styled('p')({
	margin: 0,
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingNormal,
	color: colors.textPrimary,
})

export const CartSidebarSticky = styled(Box)(({ theme }) => ({
	display: 'none',
	[theme.breakpoints.up('lg')]: {
		display: 'flex',
		flexDirection: 'column',
		position: 'sticky',
		// Figma 562:39005: cart column wrapper has py-[24px] — cart sits 24px from viewport top.
		top: spacing.xl,
		// Constrain height so the items list can scroll internally.
		// svh excludes mobile browser chrome; subtract 24px top + 24px bottom padding.
		height: `calc(100svh - ${spacing.xl}px - ${spacing.xl}px)`,
	},
}))

export const ScheduleUnavailableStatusIcon = styled(EventBusyIcon)({
	fontSize: sizing.iconSm,
	color: colors.errorAlt,
})
